import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Target, Eye, Heart, ArrowRight, Award, Users, Sparkles } from "lucide-react";

import { PageHero } from "../components/site/PageHero";
import { usePageMeta } from "../hooks/usePageMeta";

const VALUES = [
  { icon: Award, title: "Betrouwbaar", desc: "Wij komen wanneer afgesproken, communiceren transparant en houden ons aan elke afspraak." },
  { icon: Sparkles, title: "Vakkundig", desc: "Onze poetshulpen volgen interne opleidingen en werken volgens duidelijke checklists." },
  { icon: Heart, title: "Discreet", desc: "Wij respecteren uw privacy, uw spullen en uw werkomgeving — altijd." },
  { icon: Users, title: "Flexibel", desc: "Plotse opdracht of vaste planning — wij passen ons aan uw ritme aan, niet andersom." },
];

const TIMELINE = [
  { year: "2014", title: "Eerste poetscontract", desc: "Funda Mehmed start Dila Group als zelfstandige in Antwerpen-Centrum." },
  { year: "2017", title: "Eerste medewerkers", desc: "Het team breidt uit met twee vaste poetshulpen voor B2B-kantoren." },
  { year: "2021", title: "Dila Group BV", desc: "Officiële oprichting van Dila Group BV met focus op kwaliteits­schoonmaak." },
  { year: "2026", title: "Heel Antwerpen", desc: "Actief in alle wijken — particulier, kantoor en specialistische opdrachten." },
];

export default function AboutPage() {
  usePageMeta({
    title: "Over Ons — Schoonmaakbedrijf Dila Group BV uit Antwerpen",
    description:
      "Maak kennis met Dila Group BV — een Antwerps schoonmaakbedrijf met 10+ jaar ervaring, een vakkundig team en oog voor detail. Onze missie, visie en waarden.",
    path: "/over-ons",
  });

  return (
    <>
      <PageHero
        breadcrumb="Over Ons"
        eyebrow="Wie we zijn"
        title="Een gezicht en een ploeg waarop u kunt rekenen."
        intro="Dila Group BV is een onafhankelijk schoonmaakbedrijf uit hartje Antwerpen. Wij combineren vakmanschap, persoonlijke service en oog voor detail — bij elke klant, elke dag opnieuw."
      />

      {/* Story */}
      <section className="py-20 sm:py-28 bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6"
          >
            <div className="relative aspect-[5/4] rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_dila-clean/artifacts/5oktry8f_700f63d9-6d3a-4c66-be9a-a63fe588546a.jpeg"
                alt="Onze poetshulp aan het werk — Dila Group BV"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6"
          >
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#0284C7]">Onze geschiedenis</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] text-balance">
              Klein begonnen, gegroeid op vertrouwen.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Wat begon als een eenmanszaak met enkele vaste klanten in Antwerpen-Centrum, groeide uit tot een
              gevestigd schoonmaakbedrijf dat actief is in elke Antwerpse wijk. Doorheen de jaren is één principe
              nooit veranderd: <strong className="text-[#0F172A]">elke ruimte mag u even spotless terugzien als u
              ze zelf zou poetsen — en liever nog beter.</strong>
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Vandaag werkt onze ploeg voor particulieren, kantoren, praktijken, restaurants en vastgoedmakelaars.
              Klein genoeg om u persoonlijk te kennen, groot genoeg om elke opdracht aan te kunnen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team in action gallery */}
      <section className="py-20 sm:py-28 bg-[#F4F7F6]" data-testid="about-team-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#0284C7]">Ons team aan het werk</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] text-balance">
              Geen marketing­foto's, gewoon onze ploeg in actie.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Een blik achter de schermen bij echte opdrachten in Antwerpen — van detailwerk tot grote commerciële ruimtes en glazenwas.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                src: "https://customer-assets.emergentagent.com/job_dila-clean/artifacts/5oktry8f_700f63d9-6d3a-4c66-be9a-a63fe588546a.jpeg",
                caption: "Detailwerk — afstoffen tot in elk hoekje",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_dila-clean/artifacts/cnkdy6qt_4e8ab63f-6940-4147-8ebe-7708a3b62602.jpeg",
                caption: "Commerciële reiniging — Horeca Center",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_dila-clean/artifacts/g9y7p7f1_2026-05-15%20%282%29.jpg",
                caption: "Glazenwas — kraakheldere etalages",
              },
            ].map((img, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`team-photo-${i}`}
                className="group relative overflow-hidden rounded-3xl ring-1 ring-slate-200 shadow-sm aspect-[4/5] bg-white"
              >
                <img src={img.src} alt={img.caption} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-5">
                  <figcaption className="font-display font-bold text-white text-sm sm:text-base">{img.caption}</figcaption>
                </div>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & vision */}
      <section className="py-20 sm:py-28 bg-[#F4F7F6]" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-10">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-[#0284C7]">
              <Target size={22} />
            </span>
            <h3 className="mt-5 font-display text-2xl font-bold text-[#0F172A]">Onze missie</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Elke klant in Antwerpen toegang geven tot betrouwbare, kwalitatieve schoonmaak — zonder gedoe,
              zonder verrassingen en met persoonlijke aandacht voor wat u écht belangrijk vindt.
            </p>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-10">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-[#16A34A]">
              <Eye size={22} />
            </span>
            <h3 className="mt-5 font-display text-2xl font-bold text-[#0F172A]">Onze visie</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Het meest aanbevolen schoonmaakbedrijf van Antwerpen worden — niet door de grootste te zijn, maar
              door de beste relatie op te bouwen met elke klant in onze stad.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-28 bg-white" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#0284C7]">Onze waarden</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] text-balance">
              Vier principes die elke opdracht sturen.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="rounded-3xl border border-slate-200 p-6 hover:border-[#0284C7]/40 hover:shadow-md transition-all"
                data-testid={`value-card-${i}`}
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F0FDF4] text-[#16A34A]">
                  <v.icon size={20} />
                </span>
                <h3 className="mt-4 font-display text-lg font-bold text-[#0F172A]">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 sm:py-28 bg-[#0F172A] text-white overflow-hidden" data-testid="about-timeline">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#16A34A]">Mijlpalen</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-balance">
              10+ jaar bouwen aan vertrouwen.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIMELINE.map((t, i) => (
              <div key={t.year} className="relative rounded-2xl bg-white/5 border border-white/10 p-6" data-testid={`timeline-${i}`}>
                <p className="font-display text-3xl font-extrabold text-[#0284C7]">{t.year}</p>
                <h3 className="mt-2 font-display font-bold">{t.title}</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-col sm:flex-row gap-3 items-start">
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold px-6 py-3.5 shadow-lg shadow-sky-700/30 transition-all hover:-translate-y-0.5"
            >
              Plan een kennismaking <ArrowRight size={16} />
            </Link>
            <Link
              to="/diensten"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold px-6 py-3.5 transition-all"
            >
              Bekijk onze diensten
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
